import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, X, ShoppingBag, Gift, Search, AlertTriangle, Clock, CheckCircle, XCircle, Archive, RefreshCw, Trash2, Image, DollarSign, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const DISCLAIMER_TEXT = `MARKETPLACE DISCLAIMER

By using this marketplace, you acknowledge and agree that:

1. PLATFORM LIABILITY: This platform is provided as a community service only. The platform, its owners, and administrators accept NO LIABILITY for any transactions, misrepresentations, non-delivery of goods, quality issues, or any disputes between buyers and sellers.

2. USER RESPONSIBILITY: All transactions are conducted solely between buyers and sellers. Users are responsible for their own due diligence, verifying the authenticity and condition of items, and ensuring safe transaction practices.

3. NO GUARANTEES: We do not guarantee the accuracy of listings, the quality of items, or the reliability of users. We do not verify user identities or validate listings.

4. DISPUTE RESOLUTION: Any disputes must be resolved directly between the parties involved. The platform will not mediate or intervene in any disputes.

5. AT YOUR OWN RISK: All purchases, sales, and exchanges are made entirely at your own risk.

6. LISTING REMOVAL: The platform reserves the right to remove any listing at any time without notice.

By posting or purchasing through this marketplace, you accept full responsibility for your actions and release the platform from any liability.`;

const Marketplace = () => {
  const { token, user, isDemo, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddListing, setShowAddListing] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showDisclaimerBeforeListing, setShowDisclaimerBeforeListing] = useState(false);
  const [viewMode, setViewMode] = useState('browse'); // browse, my-listings
  const [filterType, setFilterType] = useState('all'); // all, sell, buy, gift
  const [searchTerm, setSearchTerm] = useState('');
  const [newListing, setNewListing] = useState({
    title: '',
    description: '',
    price: '',
    listing_type: 'sell',
    images: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [queenBatchId, setQueenBatchId] = useState(null);

  useEffect(() => {
    const batchId = searchParams.get('listQueens');
    if (!batchId || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API}/api/queen-rearing/batches/${batchId}`, { headers }).then(r => (r.ok ? r.json() : null)),
      fetch(`${API}/api/queen-rearing/breeders`, { headers }).then(r => (r.ok ? r.json() : []))
    ]).then(([batch, breeders]) => {
      if (!batch) return;
      const breeder = breeders.find(b => b.id === batch.breeder_queen_id);
      const laying = (batch.cells || []).filter(c => c.status === 'laying').length || batch.queens_laying || 0;
      const genetics = breeder?.genetics ? breeder.genetics.replace(/\b\w/g, c => c.toUpperCase()) : '';
      const yard = batch.yard_name || '';
      setNewListing(prev => ({
        ...prev,
        listing_type: 'sell',
        title: `${laying > 0 ? laying + ' ' : ''}Mated Laying Queen${laying === 1 ? '' : 's'}${genetics ? ` - ${genetics}` : ''}`,
        description: [
          'Locally reared, mated and laying queens.',
          breeder?.name ? `Breeder queen: ${breeder.name}${genetics ? ` (${genetics})` : ''}` : '',
          yard ? `Raised at: ${yard}` : '',
          batch.graft_date ? `Grafted: ${batch.graft_date}` : '',
          laying > 0 ? `Quantity available: ${laying}` : '',
          'Laying pattern checked before sale. Contact me to arrange pickup.'
        ].filter(Boolean).join('\n')
      }));
      setQueenBatchId(batchId);
      setShowDisclaimerBeforeListing(true);
    });
    searchParams.delete('listQueens');
    setSearchParams(searchParams, { replace: true });
  }, [token]);

  useEffect(() => {
    fetchListings();
    if (user) {
      fetchMyListings();
    }
  }, [user]);

  const fetchListings = async () => {
    if (!token && localStorage.getItem('demoMode') === 'true') {
      setLoading(false);
      return;
    }
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/api/marketplace/listings`, { headers });
      if (response.ok) {
        const data = await response.json();
        setListings(data);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchMyListings = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/api/marketplace/listings?status=my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMyListings(data);
      }
    } catch (err) {
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (newListing.images.length + files.length > 5) {
      toast.error('Maximum 5 images allowed');
      return;
    }

    setUploadingImage(true);
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (response.ok) {
          const data = await response.json();
          // Use file_id which includes the extension info from backend
          setNewListing(prev => ({
            ...prev,
            images: [...prev.images, data.file_id]
          }));
        }
      } catch (err) {
        toast.error('Failed to upload image');
      }
    }
    setUploadingImage(false);
  };

  const removeImage = (index) => {
    setNewListing(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    if (!newListing.title) {
      toast.error('Title is required');
      return;
    }

    try {
      const response = await fetch(`${API}/api/marketplace/listings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newListing,
          price: parseFloat(newListing.price) || 0
        })
      });

      if (response.ok) {
        toast.success('Listing submitted for approval!');
        if (queenBatchId) {
          fetch(`${API}/api/queen-rearing/batches/${queenBatchId}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ listed_for_sale: true })
          });
          setQueenBatchId(null);
        }
        setShowAddListing(false);
        setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] });
        fetchMyListings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create listing');
      }
    } catch (err) {
      toast.error('Failed to create listing');
    }
  };

  const handleMarkSold = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}/sold`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Marked as sold!');
        fetchMyListings();
        fetchListings();
      }
    } catch (err) {
      toast.error('Failed to mark as sold');
    }
  };

  const handleReinstate = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}/reinstate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing reinstated and submitted for approval');
        fetchMyListings();
      }
    } catch (err) {
      toast.error('Failed to reinstate listing');
    }
  };

  const handleHide = async (listingId) => {
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}/hide`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing hidden from public view');
        fetchMyListings();
        fetchListings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to hide listing');
      }
    } catch (err) {
      toast.error('Failed to hide listing');
    }
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm('Are you sure you want to PERMANENTLY delete this listing? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API}/api/marketplace/listings/${listingId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Listing permanently deleted');
        fetchMyListings();
        fetchListings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete listing');
      }
    } catch (err) {
      toast.error('Failed to delete listing');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Pending Approval' },
      approved: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Active' },
      denied: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Denied' },
      sold: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle, label: 'Sold' },
      archived: { bg: 'bg-stone-100', text: 'text-stone-700', icon: Archive, label: 'Archived' },
      hidden: { bg: 'bg-stone-100', text: 'text-stone-700', icon: EyeOff, label: 'Hidden' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" /> {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      sell: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: '🏷️ For Sale' },
      buy: { bg: 'bg-blue-100', text: 'text-blue-700', label: '🔍 Wanted' },
      gift: { bg: 'bg-purple-100', text: 'text-purple-700', label: '🎁 Free/Gift' },
      handyman: { bg: 'bg-orange-100', text: 'text-orange-700', label: '🔧 Handyman' }
    };
    const badge = badges[type] || badges.sell;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const filteredListings = (viewMode === 'my-listings' ? myListings : listings)
    .filter(l => filterType === 'all' || l.listing_type === filterType)
    .filter(l => !searchTerm || 
      l.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      l.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const getDaysRemaining = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const days = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <div>
      {/* Header */}
      <div className="p-4 mb-4">
        <div className="max-w-7xl flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <ShoppingBag className="h-6 w-6 text-amber-600" />
              Marketplace
            </h1>
            <p className="text-sm text-stone-500 mt-1">Buy, sell & trade beekeeping supplies</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDisclaimer(true)}
              className="text-stone-600 border-stone-300"
            >
              <AlertTriangle className="w-4 h-4 mr-2" /> Disclaimer
            </Button>
            {user && !isDemo && (
              <Button onClick={() => setShowDisclaimerBeforeListing(true)} className="bg-white hover:bg-stone-100 text-teal-700">
                <Plus className="w-4 h-4 mr-2" /> Add Listing
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4">
      <div className="max-w-7xl">

        {/* View Toggle & Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {user && !isDemo && (
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'browse' ? 'default' : 'outline'}
                  onClick={() => setViewMode('browse')}
                  size="sm"
                >
                  <ShoppingBag className="w-4 h-4 mr-1" /> Browse
                </Button>
                <Button
                  variant={viewMode === 'my-listings' ? 'default' : 'outline'}
                  onClick={() => setViewMode('my-listings')}
                  size="sm"
                >
                  My Listings ({myListings.length})
                </Button>
              </div>
            )}
            
            <div className="flex gap-2 flex-wrap">
              {['all', 'sell', 'buy', 'gift', 'handyman'].map(type => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 rounded-full text-sm ${
                    filterType === type 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  {type === 'all' ? 'All' : type === 'sell' ? '🏷️ For Sale' : type === 'buy' ? '🔍 Wanted' : type === 'gift' ? '🎁 Gifts' : '🔧 Handyman'}
                </button>
              ))}
            </div>

            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🐝</div>
            <p className="text-stone-500">Loading listings...</p>
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-stone-200">
            <div className="text-4xl mb-4">📦</div>
            <p className="text-stone-500">No listings found</p>
            {user && !isDemo && viewMode === 'my-listings' && (
              <Button onClick={() => setShowAddListing(true)} className="mt-4 bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" /> Create Your First Listing
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredListings.map(listing => (
              <div 
                key={listing.id} 
                className={`bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
                  listing.status === 'sold' ? 'opacity-60' : ''
                }`}
              >
                {/* Image */}
                <div className="h-48 bg-stone-100 relative">
                  {listing.images && listing.images.length > 0 ? (
                    <img 
                      src={`${API}/api/uploads/${listing.images[0]}`} 
                      alt={listing.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-300">
                      <Image className="w-16 h-16" />
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    {getTypeBadge(listing.listing_type)}
                  </div>
                  {listing.images && listing.images.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                      +{listing.images.length - 1} more
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-stone-800 line-clamp-1">{listing.title}</h3>
                    {listing.listing_type === 'gift' && (
                      <span className="text-lg font-bold text-purple-600">Free</span>
                    )}
                    {listing.listing_type === 'handyman' && listing.price > 0 && (
                      <span className="text-lg font-bold text-orange-600">${listing.price}/hr</span>
                    )}
                    {listing.listing_type === 'handyman' && listing.price === 0 && (
                      <span className="text-sm font-bold text-orange-600">Contact for quote</span>
                    )}
                    {listing.listing_type !== 'gift' && listing.listing_type !== 'handyman' && listing.price > 0 && (
                      <span className="text-lg font-bold text-emerald-600">${listing.price}</span>
                    )}
                  </div>
                  
                  <p className="text-sm text-stone-500 line-clamp-2 mb-3">{listing.description}</p>
                  
                  <div className="flex items-center justify-between text-xs text-stone-400">
                    <span>By {listing.user_name || 'Anonymous'}</span>
                    {viewMode === 'my-listings' && (
                      <span>{getDaysRemaining(listing.expires_at)} days left</span>
                    )}
                  </div>

                  {/* Status & Actions for My Listings */}
                  {viewMode === 'my-listings' && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <div className="flex items-center justify-between">
                        {getStatusBadge(listing.status)}
                        <div className="flex gap-1">
                          {listing.status === 'approved' && (
                            <>
                              <button
                                onClick={() => handleMarkSold(listing.id)}
                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                                title="Mark as Sold"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleHide(listing.id)}
                                className="p-1.5 text-stone-600 hover:bg-stone-100 rounded"
                                title="Hide from public"
                              >
                                <EyeOff className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {(listing.status === 'archived' || listing.status === 'denied' || listing.status === 'hidden') && (
                            <button
                              onClick={() => handleReinstate(listing.id)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Reinstate"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(listing.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete permanently"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {listing.status === 'denied' && listing.admin_comments && (
                        <p className="text-xs text-red-600 mt-2 bg-red-50 p-2 rounded">
                          Reason: {listing.admin_comments}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Contact for Browse */}
                  {viewMode === 'browse' && listing.user_email && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <a 
                        href={`mailto:${listing.user_email}?subject=Inquiry about: ${listing.title}`}
                        className="text-sm text-amber-600 hover:text-amber-700 font-medium"
                      >
                        📧 Contact Seller
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add Listing Modal */}
        {showAddListing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowAddListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800">Create Listing</h3>
                <button onClick={() => { setShowAddListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleCreateListing} className="space-y-4">
                {/* Listing Type */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'sell', label: '🏷️ For Sale', desc: 'Selling an item' },
                      { value: 'buy', label: '🔍 Wanted', desc: 'Looking to buy' },
                      { value: 'gift', label: '🎁 Gift/Free', desc: 'Giving away' },
                      { value: 'handyman', label: '🔧 Handyman', desc: 'Services offered' }
                    ].map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setNewListing({...newListing, listing_type: type.value})}
                        className={`p-3 rounded-xl border-2 transition-colors ${
                          newListing.listing_type === type.value
                            ? 'border-amber-500 bg-amber-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className="text-lg">{type.label.split(' ')[0]}</div>
                        <div className="text-xs text-stone-500">{type.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Title *</label>
                  <Input
                    data-testid="listing-title-input"
                    value={newListing.title}
                    onChange={e => setNewListing({...newListing, title: e.target.value})}
                    placeholder="e.g., Bee Suit - Size Large"
                    required
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Description</label>
                  <textarea
                    data-testid="listing-description-input"
                    value={newListing.description}
                    onChange={e => setNewListing({...newListing, description: e.target.value})}
                    placeholder="Describe your item, condition, pickup/delivery options..."
                    className="w-full p-3 border border-stone-200 rounded-xl resize-none h-24"
                  />
                </div>

                {/* Price */}
                {newListing.listing_type !== 'gift' && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      {newListing.listing_type === 'buy' ? 'Budget' : newListing.listing_type === 'handyman' ? 'Hourly Rate' : 'Price'} ($)
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-stone-400" />
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        data-testid="listing-price-input"
                        value={newListing.price}
                        onChange={e => setNewListing({...newListing, price: e.target.value})}
                        placeholder={newListing.listing_type === 'handyman' ? 'Hourly rate (or 0 for quote)' : '0.00'}
                        className="pl-10"
                      />
                    </div>
                    {newListing.listing_type === 'handyman' && (
                      <p className="text-xs text-stone-500 mt-1">Enter hourly rate or leave as 0 for &quot;Contact for quote&quot;</p>
                    )}
                  </div>
                )}

                {/* Images */}
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Images (max 5)
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newListing.images.map((img, index) => (
                      <div key={index} className="relative w-20 h-20">
                        <img
                          src={`${API}/api/uploads/${img}`}
                          alt={`Upload ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    {newListing.images.length < 5 && (
                      <label className="w-20 h-20 border-2 border-dashed border-stone-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-amber-400">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploadingImage}
                        />
                        {uploadingImage ? (
                          <div className="animate-spin w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full" />
                        ) : (
                          <Plus className="w-6 h-6 text-stone-400" />
                        )}
                      </label>
                    )}
                  </div>
                  <p className="text-xs text-stone-500">{newListing.images.length}/5 images</p>
                </div>

                {/* Notice */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  ⚠️ Your listing will be reviewed by an admin before it appears publicly. This usually takes less than 24 hours.
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowAddListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="listing-submit-btn" className="flex-1 bg-amber-600 hover:bg-amber-700">
                    Submit Listing
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Disclaimer Modal (view only) */}
        {showDisclaimer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDisclaimer(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  Marketplace Disclaimer
                </h3>
                <button onClick={() => setShowDisclaimer(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-stone-700 bg-stone-50 p-4 rounded-xl">
                  {DISCLAIMER_TEXT}
                </pre>
              </div>
              <Button onClick={() => setShowDisclaimer(false)} className="w-full mt-4">
                I Understand
              </Button>
            </div>
          </div>
        )}

        {/* Disclaimer Before Listing Modal */}
        {showDisclaimerBeforeListing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setShowDisclaimerBeforeListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }}>
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                  Please Read Before Listing
                </h3>
                <button onClick={() => { setShowDisclaimerBeforeListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-stone-700 bg-stone-50 p-4 rounded-xl">
                  {DISCLAIMER_TEXT}
                </pre>
              </div>
              <div className="flex gap-3 mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => { setShowDisclaimerBeforeListing(false); setQueenBatchId(null); if (queenBatchId) setNewListing({ title: '', description: '', price: '', listing_type: 'sell', images: [] }); }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    setShowDisclaimerBeforeListing(false);
                    setShowAddListing(true);
                  }} 
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="listing-disclaimer-agree-btn"
                >
                  I Agree - Continue
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default Marketplace;
